/*
npm i geoip-country countryjs request-ip accept-language
*/

import Prismic from 'prismic-javascript'
import { repoName, defaultLanguage } from '../config'

/*
NOTE: this relies on the following things:
- a {lang} param in the URL query
- the config file above with repoName and defaultLanguage
- a singleton called "global" in prismic that is used to determine available languages
*/

/**
 * Takes a App.getInitialProps-appContext and returns a language to be used through multiple methods
 * @param {Object} appContext
 */
export default async function getLanguage(appContext) {
  // first check if there's one in the URL already
  let languageFromURL = getLanguageFromURL(appContext)
  if (languageFromURL) {
    // no need to clean a language that came from a url
    return cleanLanguage(languageFromURL)
  }
  // it should never be the case that we don't have a language in the URL on the client
  if (typeof window === 'undefined') {
    // if there was no preference in the URL, we first have to know which languages are available on the server
    let languagesFromServer = await getLanguagesFromServer()
    // move german to the front if it exists
    if (languagesFromServer.includes('de-de')) {
      languagesFromServer = languagesFromServer.filter(item => item !== 'de-de')
      languagesFromServer.unshift('de-de')
    }
    // now let's first try geoip as it's pretty accurate
    const languagesFromRequest = getLanguagesFromRequest(appContext)
    if (languagesFromServer && languagesFromRequest) {
      const finalLangFromServer = cleanLanguage(chooseLanguage(languagesFromServer, languagesFromRequest))
      if (finalLangFromServer) {
        return finalLangFromServer
      }
    }
    // if that for some reason is not working, lets try the request header
    if (appContext.ctx.req.headers['accept-language']) {
      const acceptLanguage = require('accept-language')
      const languagesFromServerForCheck = languagesFromServer.map(lang => convertPrismicToLang(lang))
      // set the languages we have for matching
      acceptLanguage.languages(languagesFromServerForCheck)
      // improve german detection
      let acceptHeader = appContext.ctx.req.headers['accept-language']
      if (appContext.ctx.req.headers['accept-language'].length === 2) {
        acceptHeader = `${appContext.ctx.req.headers['accept-language']}-${
          appContext.ctx.req.headers['accept-language']
        }`
      }
      // acceptLanguage already matches, so we can skip chooseLanguage()
      const matchedLanguage = acceptLanguage.get(acceptHeader)
      const finalLangFromBrowser = cleanLanguage(matchedLanguage)
      if (finalLangFromBrowser) {
        return finalLangFromBrowser
      }
    }
  }
  // if nothing else worked, return the defaultlanguage
  return defaultLanguage
}

/**
 * Converts a prismic formatted language to consistent 4-digit language codes
 * @param {String} lang
 */
export function convertPrismicToLang(lang) {
  let language = lang
  if (lang === 'ro') {
    language = 'ro-ro'
  }
  if (lang === 'ru') {
    language = 'ru-ru'
  }
  return language
}

/**
 * Converts a 4-digit language code to prismics formats
 * @param {String} lang
 */
export function convertLangToPrismic(lang) {
  let language = lang
  // prismic has two letter ro and ru, dont ask me why
  if (language === 'ru-ru') {
    language = 'ru'
  }
  if (language === 'ro-ro') {
    language = 'ro'
  }
  return language
}

/**
 * Tries to pick a language from preferred Languages that are also available
 * @param {Array} availableLanguages
 * @param {Array} preferredLanguages
 */
function chooseLanguage(availableLanguages, preferredLanguages) {
  let foundLanguage
  for (let i = 0; i < preferredLanguages.length; i++) {
    // lang can be two or four digits and a hyphen
    if (preferredLanguages[i].length === 5) {
      foundLanguage = availableLanguages.find(aL => aL === preferredLanguages[i])
      break
    } else if (preferredLanguages[i].length === 2) {
      foundLanguage = availableLanguages.find(aL => aL.substr(0, 2) === preferredLanguages[i])
      break
    }
  }
  return foundLanguage
}

/**
 * Can be used to manually override languages at the very end.
 * @param {String} language
 */
function cleanLanguage(language) {
  // let's have spanish for catalan people
  if (language === 'ca' || language === 'ca-es') {
    language = 'es-es'
  }
  return convertLangToPrismic(language)
}

/**
 * Fires a request to prismic to look for all global documents, then reduces them to their languages
 */
async function getLanguagesFromServer() {
  const api = await Prismic.getApi(`https://${repoName}.prismic.io/api/v2`)
  // we qualify the global article being available in a language as a language existing
  let response = await api.query(Prismic.Predicates.at('document.type', 'global'), { lang: '*' })
  const availableLanguages = response.results.map(r => r.lang)
  return availableLanguages
}

/**
 * Reads a possible language query from the url
 * @param {Object} appContext
 */
function getLanguageFromURL(appContext) {
  if (appContext && appContext.router && appContext.router.query && appContext.router.query.lang) {
    return appContext.router.query.lang
  }
}

/**
 * Either returns a 4 digit code or null if it wasnt successfull
 * @param {Object} appContext
 */
function getLanguagesFromRequest(appContext) {
  const requestIp = require('request-ip')
  const ip = requestIp.getClientIp(appContext.ctx.req)
  return getLanguagesFromIP(ip)
}

/**
 * Given a valid IP, will return languages from the country that IP is from
 * @param {String} ip
 */
function getLanguagesFromIP(ip) {
  if (typeof window === 'undefined') {
    // step 1: get the country from the ip
    const geoip = require('geoip-country')
    const geoFromIP = geoip.lookup(ip)
    if (!geoFromIP) {
      return null
    } else {
      // step 2: resolve languages for that country, like: 'de'
      const country = require('countryjs')
      const languages = country.languages(geoFromIP.country.toUpperCase())
      // step 3: prepare country+language combinations as well, like: 'de-at'
      const countryLangs = languages.map(lang => `${lang}-${geoFromIP.country.toLowerCase()}`)
      return countryLangs.concat(languages)
    }
  }
}
